import { $ } from "./core";
import { audioInit, coinClink } from "./audio";
import { renderBal, unlockIfNeeded, closeDeposit, setBal, setDepositing, onWalletAccount } from "./core-game";
import * as ethers from "ethers";
import { CHAIN_CONFIG as GAME_CHAIN } from "./chain/config";
import { isTelegram, isTouchDevice, openExternal } from "./mobile";
import { isOnchainConfigured } from "./chain/config";

/* ================= wallet connect + on-chain hooks =================
   Injected wallets (MetaMask, Rabby, anything EIP-6963) are discovered
   automatically. WalletConnect activates once walletConnectProjectId is set.

   Chain settings come from ./chain/config — deliberately NOT duplicated here.
   They used to be, and the two copies drifted: this layer still said Robinhood
   TESTNET (0xb626) and switched the wallet there, while ScratchyGame lives on
   mainnet (0x1237). Deposits were then sent to an address with no code on that
   chain, which surfaces as an unexplained "missing revert data". One source of
   truth is the fix. */
const CHAIN_CONFIG={
  /* legacy MoonScratchVault path — only used when a vault address is set */
  vaultAddress:"",
  usdcAddress:"",
  chainId:GAME_CHAIN.chainId,
  chainName:GAME_CHAIN.chainName,
  rpcUrl:GAME_CHAIN.rpcUrl,
  explorer:GAME_CHAIN.explorer,
  chainIdNumber:GAME_CHAIN.chainIdNumber,
  nativeCurrency:GAME_CHAIN.nativeCurrency,
  walletConnectProjectId:GAME_CHAIN.walletConnectProjectId
};

const W={provider:null,account:null,chainId:null,label:""};
const found=new Map();

/* These must be initialised BEFORE the eip6963 handshake below.
   dispatchEvent() is synchronous, and wallets that announce synchronously
   (MetaMask and friends) call renderWalletList() right back — which reads
   wList. With the lookups declared after the dispatch, that read landed in
   the temporal dead zone and threw
   "ReferenceError: Cannot access 'wList' before initialization",
   once per installed wallet, losing the first render of the list. */

/**
 * Eager reconnect.
 *
 * eth_accounts does NOT prompt — it reports an account only if this origin was
 * already authorised. Without this, a reload left the app disconnected with a
 * balance of 0 while the player's funds sat in the contract, and the only way
 * back was to click CONNECT again. Runs shortly after the EIP-6963 handshake so
 * announced providers have landed.
 */
function eagerReconnect(){
  setTimeout(async()=>{
    if(W.account)return;
    const candidates=[...found.values()].map(f=>({p:f.provider,name:f.info.name}));
    if(!candidates.length&&window.ethereum)candidates.push({p:window.ethereum,name:"Browser wallet"});
    for(const c of candidates){
      try{
        const accs=await c.p.request({method:"eth_accounts"});
        if(accs&&accs.length){ adoptProvider(c.p,accs[0],c.name); return; }
      }catch(e){ /* provider not ready or not authorised — try the next */ }
    }
    /* A WalletConnect session survives a reload, and on mobile that matters
       more than anywhere: deep-linking into the wallet leaves this page, and
       the OS may well have discarded it by the time the player comes back.
       Only attempted when a session is actually stored, so players who never
       used WalletConnect pay neither the import nor a relay connection. */
    if(CHAIN_CONFIG.walletConnectProjectId&&hasStoredWcSession()){
      try{ await restoreWalletConnect(); }catch(e){ /* stale session — ignore */ }
    }
  },300);
}

function hasStoredWcSession(){
  try{
    for(let i=0;i<localStorage.length;i++){
      const k=localStorage.key(i);
      if(k&&k.startsWith("wc@2:")&&k.includes("session"))return true;
    }
  }catch(e){ /* storage blocked (private mode) — nothing to restore */ }
  return false;
}

const wModal=$("walletModal"), wList=$("wList"), wcBtn=$("wcBtn");

window.addEventListener("eip6963:announceProvider",e=>{found.set(e.detail.info.uuid,e.detail);renderWalletList();});
window.dispatchEvent(new Event("eip6963:requestProvider"));
eagerReconnect();
function shortAddr(a){return a.slice(0,6)+"…"+a.slice(-4);}
function closeWallets(){wModal.classList.remove("on");}
$("wCloseBtn").onclick=closeWallets;
wModal.addEventListener("click",e=>{if(e.target===wModal)closeWallets();});
wcBtn.onclick=()=>{audioInit&&audioInit(); if(W.account)disconnectWallet(); else {renderWalletList();wModal.classList.add("on");}};

function walletRow(icon:string,label:string,sub?:string){
  const b=document.createElement("button");
  b.className="wrow";
  b.innerHTML=`<span>${icon}</span><span>${label}${sub?`<span class="sub3">${sub}</span>`:""}</span>`;
  return b;
}

function renderWalletList(){
  if(!wList)return;
  wList.innerHTML="";

  /* Injected providers first — on desktop, and inside a wallet's own browser
     on mobile, this is the whole story. */
  found.forEach(({info,provider})=>{
    const b=document.createElement("button");b.className="wrow";
    const img=document.createElement("img");img.src=info.icon;img.alt="";
    const sp=document.createElement("span");sp.textContent=info.name;
    b.append(img,sp);
    b.onclick=()=>connectInjected(provider,info.name);
    wList.appendChild(b);
  });
  if(!found.size && window.ethereum){
    const b=walletRow("🧩","Browser wallet");
    b.onclick=()=>connectInjected(window.ethereum,"Browser wallet");
    wList.appendChild(b);
  }

  const hasInjected=found.size>0||!!window.ethereum;
  const hasWc=!!CHAIN_CONFIG.walletConnectProjectId;
  const mobile=isTouchDevice();

  /* With a project id configured, WalletConnect is the better mobile path and
     goes first: it hands off to the wallet app and hands back, so the player
     stays in THIS page — and so in Telegram they stay inside the mini-app.
     Reopening the game in a wallet's own browser (below) abandons that session
     and, in Telegram, the whole mini-app context. */
  if(hasWc){
    const primary=mobile&&!hasInjected;
    const wc=walletRow("🔗", primary?"Connect a wallet app":"WalletConnect",
      primary?"MetaMask, Trust, Rainbow…":"scan or pick an app");
    if(primary)wc.classList.add("wrow-primary");
    wc.onclick=()=>connectWalletConnect();
    wList.appendChild(wc);
  }

  const note=$("wNote");
  if(hasInjected){
    note.textContent="";
  }else if(mobile){
    /* The only mobile path now. Reopening the game inside a wallet's own
       browser used to be offered here as a fallback; it abandons this page,
       and in Telegram abandons the mini-app, so the player never comes back
       to where they started. */
    note.textContent=hasWc
      ? "Connect a wallet app to continue — you'll approve in the wallet and come straight back."
      : "No wallet available in this browser. Open the game in a browser with a wallet extension.";
  }else{
    note.textContent=hasWc
      ? "No browser wallet detected. Use WalletConnect above, or install MetaMask or Rabby."
      : "No browser wallet detected. Install MetaMask or Rabby to connect.";
  }
}

async function connectInjected(provider,label){
  try{
    const accs=await provider.request({method:"eth_requestAccounts"});
    adoptProvider(provider,accs[0],label);
  }catch(e){ $("wNote").textContent=(e&&e.message)||"Connection rejected."; }
}
function adoptProvider(provider,account,label){
  W.provider=provider;W.account=account;W.label=label;
  provider.request({method:"eth_chainId"}).then(id=>{W.chainId=id;paintWallet();}).catch(()=>{});
  if(provider.on){
    provider.on("accountsChanged",a=>{(a&&a.length)?(W.account=a[0],paintWallet(),onWalletAccount()):disconnectWallet();});
    provider.on("chainChanged",id=>{W.chainId=id;paintWallet();});
    provider.on("disconnect",()=>disconnectWallet());
  }
  closeWallets();paintWallet();
  /* pull the on-chain balance in as soon as we know who is connected */
  onWalletAccount();
}
function disconnectWallet(){
  try{W.provider&&W.provider.disconnect&&W.provider.disconnect();}catch(e){}
  W.provider=null;W.account=null;W.chainId=null;W.label="";paintWallet();onWalletAccount();
}
function paintWallet(){
  if(W.account){
    const wrong=CHAIN_CONFIG.chainId&&W.chainId&&W.chainId.toLowerCase()!==CHAIN_CONFIG.chainId.toLowerCase();
    wcBtn.textContent=(wrong?"⚠ ":"")+shortAddr(W.account);
    wcBtn.classList.add("on");
    wcBtn.title=wrong?("Wrong network. Actions will prompt a switch to "+CHAIN_CONFIG.chainName+". Click to disconnect.")
                     :(W.label+". Click to disconnect");
  }else{
    wcBtn.textContent="CONNECT";wcBtn.classList.remove("on");wcBtn.title="Connect a wallet";
  }
  const dw=$("depWallet");
  if(dw){
    dw.style.display=W.account?"block":"none";
    /* onchainReady() is the LEGACY MoonScratchVault check and its addresses are
       intentionally blank now, so it is always false. Using it here told players
       "demo credit (no contract configured)" while the button went on to move
       real ETH — the most misleading thing the UI could say. */
    const live=isOnchainConfigured()&&!!W.account&&!!W.provider;
    dw.textContent=W.account
      ?("Wallet: "+shortAddr(W.account)+(live?" · deposits settle on-chain in real ETH":" · demo credit only"))
      :"";
  }
}
function isSameChain(){
  return !CHAIN_CONFIG.chainId||
    (!!W.chainId&&W.chainId.toLowerCase()===CHAIN_CONFIG.chainId.toLowerCase());
}
async function ensureChain(){
  if(isSameChain())return true;
  try{ await W.provider.request({method:"wallet_switchEthereumChain",params:[{chainId:CHAIN_CONFIG.chainId}]});
       W.chainId=CHAIN_CONFIG.chainId; return true; }
  catch(e){
    if(e&&(e.code===4902||(e.data&&e.data.originalError&&e.data.originalError.code===4902))){
      await W.provider.request({method:"wallet_addEthereumChain",params:[{
        chainId:CHAIN_CONFIG.chainId,chainName:CHAIN_CONFIG.chainName,
        rpcUrls:[CHAIN_CONFIG.rpcUrl],blockExplorerUrls:[CHAIN_CONFIG.explorer],
        nativeCurrency:CHAIN_CONFIG.nativeCurrency}]});
      W.chainId=CHAIN_CONFIG.chainId; return true;
    }
    return false;
  }
}
/**
 * WalletConnect v2.
 *
 * Driven from the raw `display_uri` rather than the SDK's bundled modal.
 *
 * `showQrModal:true` does work — the modal ships as @reown/appkit, a hard
 * dependency of the provider — but it is the wrong fit here on three counts:
 *
 *   - Telegram. The modal opens deep links its own way; Telegram's webview
 *     traps that, and the wallet has no route back. Only Telegram's openLink
 *     escapes, and openExternal() is what calls it.
 *   - Weight. The modal is a lazily-imported ~300kB of appkit chunks. Left
 *     unused they are built but never fetched.
 *   - CSP. It pulls fonts and telemetry from further origins that would each
 *     have to be allowed in two places.
 *
 * Handling the URI ourselves means a phone jumps straight into the wallet app
 * and a desktop gets a QR drawn locally.
 */
const WC_DEEPLINKS:{name:string;icon:string;uri:(u:string)=>string}[]=[
  /* An empty uri means "just bring the app forward", which is what the
     post-connect chain prompt needs — the request is already on the relay. */
  {name:"MetaMask", icon:"🦊", uri:u=>u?`https://metamask.app.link/wc?uri=${encodeURIComponent(u)}`:"https://metamask.app.link/"},
  {name:"Trust",    icon:"🛡", uri:u=>`https://link.trustwallet.com/wc?uri=${encodeURIComponent(u)}`},
  {name:"Rainbow",  icon:"🌈", uri:u=>`https://rnbwapp.com/wc?uri=${encodeURIComponent(u)}`}
];

let wcProvider:any=null;

/** Escape hatch from the pairing view — otherwise the list is a dead end. */
function wcBackRow(){
  const b=walletRow("←","Back","choose another way to connect");
  b.onclick=()=>renderWalletList();
  return b;
}

function wcShowUri(uri:string){
  const note=$("wNote");
  wList.innerHTML="";

  if(isTouchDevice()){
    /* Straight into the wallet app with the pairing request attached. */
    WC_DEEPLINKS.forEach(w=>{
      const b=walletRow(w.icon,`Continue in ${w.name}`,"opens the wallet to approve");
      b.onclick=()=>openExternal(w.uri(uri));
      wList.appendChild(b);
    });
    wList.appendChild(wcBackRow());
    note.textContent="Approve the connection in your wallet, then come back here.";
    /* Auto-launch the most common one — one tap instead of two. */
    openExternal(WC_DEEPLINKS[0].uri(uri));
    return;
  }

  /* Desktop: render the QR locally. Generated in-page as a data URI, so no
     third-party image host and nothing new for the CSP to allow. */
  const wrap=document.createElement("div");
  wrap.className="wcqr";
  wrap.innerHTML='<div class="wcqr-hint">Scan with your wallet app</div>';
  wList.appendChild(wrap);
  import("qrcode").then(async QR=>{
    const url=await QR.toDataURL(uri,{width:240,margin:1,color:{dark:"#0B0B0D",light:"#F4F7F2"}});
    const img=document.createElement("img"); img.src=url; img.alt="WalletConnect QR code";
    wrap.appendChild(img);
  }).catch(()=>{ wrap.innerHTML='<div class="wcqr-hint">Could not draw the QR code.</div>'; });

  const copy=walletRow("📋","Copy connection link","paste into your wallet");
  copy.onclick=async()=>{
    try{ await navigator.clipboard.writeText(uri); note.textContent="Link copied."; }
    catch(e){ note.textContent="Copy failed — long-press the QR instead."; }
  };
  wList.appendChild(copy);
  wList.appendChild(wcBackRow());
  note.textContent="Scan the code, or copy the link into your wallet.";
}

/**
 * Re-adopt a stored session without showing any pairing UI.
 *
 * `EP.init` rehydrates from localStorage, so if the session is still live the
 * accounts are simply there and no wallet round trip happens at all.
 */
async function restoreWalletConnect(){
  const { EthereumProvider: EP } = await import("@walletconnect/ethereum-provider");
  const chainId=parseInt(CHAIN_CONFIG.chainId,16);
  const p=await EP.init({
    projectId:CHAIN_CONFIG.walletConnectProjectId,
    chains:[], optionalChains:[chainId], showQrModal:false,
    rpcMap:{[chainId]:CHAIN_CONFIG.rpcUrl}
  });
  const accs=p.accounts||[];
  if(!p.session||!accs.length){ try{ await p.disconnect(); }catch(e){} return; }
  p.on("disconnect",()=>{ wcProvider=null; disconnectWallet(); });
  p.on("session_delete",()=>{ wcProvider=null; disconnectWallet(); });
  wcProvider=p;
  adoptProvider(p,accs[0],"WalletConnect");
}

/**
 * Bring the wallet app forward when a confirmation is waiting in it.
 *
 * Over WalletConnect the request travels the relay and surfaces inside the
 * WALLET, not this page. The player is looking at "Confirm in your wallet…"
 * with nothing to tap and no indication that another app wants them — so send
 * them there. Pointless for an injected provider, which draws its own prompt
 * over this page, and pointless on desktop where both are already visible.
 */
export function nudgeWallet(){
  if(!isTouchDevice())return;
  if(W.label!=="WalletConnect")return;
  /* Empty uri = "just open the app"; the request is already on the relay. */
  openExternal(WC_DEEPLINKS[0].uri(""));
}

async function connectWalletConnect(){
  const note=$("wNote");
  note.textContent="Starting WalletConnect…";
  try{
    /* Code-split: only fetched from our own origin when actually chosen. */
    const { EthereumProvider: EP } = await import("@walletconnect/ethereum-provider");
    const chainId=parseInt(CHAIN_CONFIG.chainId,16);

    /* Clear any half-finished attempt, or its relay session lingers. */
    if(wcProvider){ try{ await wcProvider.disconnect(); }catch(e){} wcProvider=null; }

    const p=await EP.init({
      projectId:CHAIN_CONFIG.walletConnectProjectId,
      /* Robinhood Chain is not a chain most wallets know. Requiring it in
         `chains` makes them refuse the session outright; as an OPTIONAL chain
         they connect first and we prompt the switch afterwards. */
      chains:[],
      optionalChains:[chainId],
      showQrModal:false,
      rpcMap:{[chainId]:CHAIN_CONFIG.rpcUrl},
      metadata:{
        name:"Scratchy",
        description:"Instant-win scratch cards on Robinhood Chain",
        url:location.origin,
        icons:[location.origin+"/favicon.ico"]
      }
    });

    p.on("display_uri",(uri:string)=>wcShowUri(uri));
    p.on("disconnect",()=>{ wcProvider=null; disconnectWallet(); });
    p.on("session_delete",()=>{ wcProvider=null; disconnectWallet(); });

    try{
      await p.connect();
    }catch(err){
      /* A cancelled or timed-out pairing leaves the relay session open. */
      try{ await p.disconnect(); }catch(e2){}
      throw err;
    }

    const accs=p.accounts||[];
    if(!accs.length)throw new Error("Wallet connected but returned no account.");
    wcProvider=p;
    adoptProvider(p,accs[0],"WalletConnect");

    /* The session comes back on whatever chain the wallet had open, so the
       switch still has to be asked for. Over WalletConnect that prompt appears
       in the WALLET APP, not here — on a phone the player is looking at this
       page and sees nothing, so send them back across first. */
    if(!isSameChain()){
      note.textContent=`Approve switching to ${CHAIN_CONFIG.chainName} in your wallet…`;
      if(isTouchDevice())openExternal(WC_DEEPLINKS[0].uri(""));
      const ok=await ensureChain();
      note.textContent=ok?"":
        `Connected, but your wallet is not on ${CHAIN_CONFIG.chainName}. `+
        `Add it manually (RPC ${CHAIN_CONFIG.rpcUrl}, chain id ${CHAIN_CONFIG.chainIdNumber}) and reconnect.`;
      if(!ok)return;
    }
    closeWallets();
  }catch(e:any){
    const msg=String(e?.message||e||"");
    note.textContent=/rejected|closed|cancel/i.test(msg)
      ? "Connection cancelled."
      : (msg.slice(0,120)||"WalletConnect failed.");
  }
}

/* --------- on-chain module: live once addresses are configured --------- */
const VAULT_ABI=[
  "function deposit(uint256 amount)",
  "function withdraw(uint256 amount)",
  "function buyCard(uint8 tierId, bytes32 clientSeed) returns (uint256)",
  "function balances(address) view returns (uint256)",
  "event CardResolved(uint256 indexed cardId,address indexed player,uint256 prize,bytes32 layoutSeed,uint256 randomness,bytes32 clientSeed,uint8 tierId)"
];
const ERC20_ABI=[
  "function approve(address spender,uint256 amount) returns (bool)",
  "function allowance(address owner,address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function faucet()" // TestUSDC public faucet — testnet only ($100, once an hour)
];
/* ethers is bundled, not fetched at runtime. A CDN <script> in the transaction
   -signing path means a CDN compromise can silently re-target transfers, and it
   forces script-src to trust a third-party origin. Pinned in package.json. */
async function loadEthers(){ return ethers; }
function onchainReady(){return !!(CHAIN_CONFIG.vaultAddress&&CHAIN_CONFIG.usdcAddress&&W.account&&W.provider);}
async function chainCtx(){
  const E=await loadEthers();
  const bp=new E.BrowserProvider(W.provider);
  const signer=await bp.getSigner();
  return {E,bp,signer,
    vault:new E.Contract(CHAIN_CONFIG.vaultAddress,VAULT_ABI,signer),
    usdc:new E.Contract(CHAIN_CONFIG.usdcAddress,ERC20_ABI,signer)};
}
window.__onchain={
  ready:onchainReady,
  /// Open the wallet picker. core-game routes its "no wallet yet" dead ends
  /// here rather than printing advice the player cannot act on.
  connect(){ audioInit&&audioInit(); renderWalletList(); wModal.classList.add("on"); },
  /// Bring the wallet app forward when a signature is pending in it.
  nudge:()=>nudgeWallet(),
  /// Connected wallet address, or null. core-game routes on-chain plays by this.
  account:()=>W.account,
  /// The raw EIP-1193 provider, for the ScratchyGame client in src/chain/.
  provider:()=>W.provider,
  /// Prompt the wallet onto the configured chain (adding it if unknown).
  switchChain:()=>ensureChain(),
  /* Testnet faucet: mint $100 TestUSDC to the connected wallet (once an hour).
     TESTNET ONLY — TestUSDC has no real-world value. */
  async faucet(){
    const st=$("depState");
    try{
      if(!CHAIN_CONFIG.usdcAddress){st.textContent="No testnet token configured";return;}
      if(!W.account||!W.provider){st.textContent="Connect a wallet first to get test funds";return;}
      if(!await ensureChain())throw new Error("Switch to "+CHAIN_CONFIG.chainName+" for test funds");
      const c=await chainCtx();
      st.textContent="Confirm in your wallet…";
      const tx=await c.usdc.faucet();
      st.textContent="Requesting test USDC… "+tx.hash.slice(0,10)+"…";
      await tx.wait();
      st.textContent="✓ $100 test USDC sent — now deposit to play";
    }catch(e){ st.textContent=(e&&(e.shortMessage||e.message))||"Faucet request failed"; }
  },
  /* "+ ADD" path: approve if needed, then vault.deposit; the play balance
     then mirrors vault.balances(account) — the vault is the source of truth. */
  async deposit(usd){
    const st=$("depState");
    try{
      setDepositing(true);
      if(!await ensureChain())throw new Error("Switch to "+CHAIN_CONFIG.chainName+" to deposit");
      const c=await chainCtx();
      const dec=await c.usdc.decimals();
      const amt=c.E.parseUnits(String(usd),dec);
      const allowance=await c.usdc.allowance(W.account,CHAIN_CONFIG.vaultAddress);
      if(allowance<amt){
        st.textContent="Approve in your wallet…";
        await (await c.usdc.approve(CHAIN_CONFIG.vaultAddress,amt)).wait();
      }
      st.textContent="Confirm deposit in your wallet…";
      const tx=await c.vault.deposit(amt);
      st.textContent="Depositing… "+tx.hash.slice(0,10)+"…";
      await tx.wait();
      const onBal=await c.vault.balances(W.account);
      setBal(Number(c.E.formatUnits(onBal,dec))); renderBal(true); coinClink(8);
      window.__ref&&window.__ref.onDeposit(usd);
      st.textContent="✓ Deposit confirmed on-chain";
      unlockIfNeeded(); setTimeout(closeDeposit,900);
    }catch(e){ st.textContent=(e&&(e.shortMessage||e.message))||"Deposit failed"; }
    finally{ setDepositing(false); }
  },
  /* Integration hooks for the rest of the loop (GO-LIVE Phase 2, step 5):
     buy on-chain, listen for CardResolved, verify with verifier.js, render
     the grid from layoutSeed — the scratch reveal stays pure theatre. */
  async buyCard(tierId){
    if(!await ensureChain())throw new Error("wrong chain");
    const c=await chainCtx();
    const seed=c.E.hexlify(c.E.randomBytes(32)); // client entropy — always random
    const tx=await c.vault.buyCard(tierId,seed);
    return {seed,receipt:await tx.wait()};
  },
  async withdraw(usd){
    if(!await ensureChain())throw new Error("wrong chain");
    const c=await chainCtx(); const dec=await c.usdc.decimals();
    const tx=await c.vault.withdraw(c.E.parseUnits(String(usd),dec));
    return tx.wait();
  },
  async subscribeResolved(cb){
    const c=await chainCtx();
    c.vault.on("CardResolved",(cardId,player,prize,layoutSeed,randomness,clientSeed,tierId)=>{
      if(W.account&&player.toLowerCase()===W.account.toLowerCase())
        cb({cardId,prize,layoutSeed,randomness,clientSeed,tierId});
    });
  }
};
paintWallet();
